import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { useOrdersReportApi } from '@hooks/api';
import { asCurrency } from '@utils/number-util';

import config from '../config';

import type { QueryKey } from 'react-query';
import type { OrderSearchRequest } from '@goodfynd/types';
import type { OrdersReportTrendResponse } from '../services/api/types';
type ReportTrendOptions = {
  onError?: (err: unknown) => void;
  onSuccess?: (
    data: OrdersReportTrendResponse,
    toggleBusy: Dispatch<SetStateAction<boolean>>
  ) => void;
  queryKey?: QueryKey;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
  request: OrderSearchRequest;
  updateSearch?: () => void;
};

export function useReportTrend({
  onError,
  onSuccess,
  queryKey = config.queryKeys.ordersReportTrend,
  refetchInterval,
  refetchOnWindowFocus = false,
  request,
}: ReportTrendOptions) {
  const api = useOrdersReportApi();
  const [busy, toggleBusy] = useState(false);

  const { data, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    OrdersReportTrendResponse,
    unknown
  >(
    [queryKey, request],
    async () => {
      return await api.trend(request);
    },
    {
      enabled: !!request,
      onError,
      onSuccess(data) {
        onSuccess?.(data, toggleBusy);
      },
      refetchInterval,
      refetchOnWindowFocus,
    }
  );

  const results = useMemo(
    () => ({
      ...data,
      total: data?.total && asCurrency(data.total),
    }),
    [data]
  );

  return {
    ...results,
    isError,
    isFetched,
    isFetching: isFetching || busy,
    isLoading,
    refetch,
  };
}
